import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Map, Truck, CheckCircle2 } from "lucide-react";
import { customerLogos } from "@/data/customerLogos";

export function FeaturedCase() {
  const { t } = useTranslation();

  return (
    <section className="py-20 lg:py-28 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="inline-block text-sm font-bold font-subheading uppercase tracking-widest text-coral mb-3">
              {t("customerCases.featured.tagline")}
            </span>
            <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight mb-6">
              {t("customerCases.featured.title")}
            </h2>
            <p className="text-muted-foreground font-body leading-relaxed mb-6">
              {t("customerCases.featured.description")}
            </p>
            <p className="text-muted-foreground font-body leading-relaxed mb-8">
              {t("customerCases.featured.result")}
            </p>

            <div className="grid grid-cols-3 gap-4 mb-8">
              {(["efficiency", "emissions", "satisfaction"] as const).map((key) => (
                <div key={key} className="bg-secondary/50 rounded-lg p-4 text-center">
                  <div className="w-10 h-10 rounded-full bg-coral/10 flex items-center justify-center mx-auto mb-2">
                    <CheckCircle2 className="h-5 w-5 text-coral" />
                  </div>
                  <p className="text-sm font-bold font-subheading text-primary">
                    {t(`customerCases.featured.stats.${key}.value`)}
                  </p>
                  <p className="text-xs text-muted-foreground font-body mt-1">
                    {t(`customerCases.featured.stats.${key}.label`)}
                  </p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-petrol-light text-primary px-3 py-1.5 rounded-full">
                <Map className="h-3 w-3" /> Atlas
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-petrol-light text-primary px-3 py-1.5 rounded-full">
                <Truck className="h-3 w-3" /> Transport & Logistik
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="flex justify-center"
          >
            <div className="bg-card rounded-2xl p-10 border border-border shadow-lg max-w-md w-full">
              <img
                src={customerLogos.find(l => l.name === "Best Transport")?.src}
                alt="Best Transport"
                width={120}
                height={48}
                className="h-12 w-auto object-contain mb-6"
                loading="lazy"
              />
              <h3 className="text-lg font-bold font-subheading text-foreground mb-2">
                {t("customerCases.featured.company")}
              </h3>
              <p className="text-sm text-muted-foreground font-body mb-4">
                {t("customerCases.featured.companyDesc")}
              </p>
              <div className="space-y-2">
                {(["challenge", "solution", "outcome"] as const).map((key) => (
                  <div key={key} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-coral mt-0.5 shrink-0" />
                    <p className="text-sm text-foreground font-body">
                      {t(`customerCases.featured.bullets.${key}`)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
